// Mobx
import { action, computed, observable } from "mobx";
// Api
// RootStore
import { Store } from "#/store";
import { Api } from "#/api";
import { ConfigSelectionResult } from "#/common/models/type_selection/ConfigSelectionResult";
import { TypeSelectionResult } from "#/common/models/type_selection/TypeSelectionResult";
import { CatalogSelectionResult } from "#/common/models/type_selection/CatalogSelectionResult";

// Models

export class TypeSelectionFilterState {
  @observable
  public klasse?: number;

  @observable
  public hasConfigSelection = false;

  @observable
  public config = observable.map({});

  @observable
  public filter = observable.map({});

  @observable
  public sa_data = observable.map({});

  @observable
  public codechain = observable.map({});

  @observable
  public configSelection?: ConfigSelectionResult;

  @observable
  public typeSelection?: TypeSelectionResult;

  @observable
  public catalogSelection?: CatalogSelectionResult;

  @observable
  isConfigSelectionLoading = false;

  @observable
  isTypeSelectionLoading = false;

  @observable
  isCatalogSelectionLoading = false;

  private token?: string;

  // RootStore
  public rootStore: Store;

  public constructor(store: Store) {
    this.rootStore = store;

    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get("token")) {
      this.token = urlParams.get("token")!;
    }
  }

  @computed
  public get activeCatalogs() {
    if (this.catalogSelection && this.catalogSelection.entries) {
      return this.catalogSelection.entries
        .filter((entry) => entry.required_filter_satisfied)
        .filter(
          (entry) =>
            entry.count > 0 ||
            (entry.count == 0 && entry.count_nouserfilter == 0)
        )
        .map((entry) => entry.id);
    } else {
      return [];
    }
  }

  public getCatalogIds() {
    if (this.catalogSelection && this.catalogSelection.entries) {
      return this.catalogSelection.entries.map((entry) => entry.id);
    } else {
      return [];
    }
  }

  @computed
  public get selectFields() {
    if (this.typeSelection && this.typeSelection.selects) {
      return this.typeSelection.selects;
    } else {
      return [];
    }
  }

  @action.bound
  public async loadFilterSession() {
    if (this.token) {
      let session = await Api.katalogService.getSession(this.token);

      if (session && session["klasse"]) {
        if (session["klasse"] != this.klasse) {
          this.klasse = session["klasse"];
        }
      } else {
        this.klasse = undefined;
      }

      if (session && session["config"]) {
        if (session["config"] != this.config) {
          this.config.replace(session["config"]);
        }
      } else {
        this.config.replace({});
      }

      if (session && session["filter"]) {
        if (session["filter"] != this.filter) {
          this.filter.replace(session["filter"]);
        }
      } else {
        this.filter.replace({});
      }

      if (session && session["sa_data"]) {
        if (session["sa_data"] != this.sa_data) {
          this.sa_data.replace(session["sa_data"]);
        }
      } else {
        this.sa_data.replace({});
      }

      if (session && session["codechain"]) {
        if (session["codechain"] != this.codechain) {
          this.codechain.replace(session["codechain"]);
        }
      } else {
        this.codechain.replace({});
      }
    }
  }

  @action.bound
  public async loadConfigSelection() {
    if (this.klasse) {
      this.isConfigSelectionLoading = true;
      this.isTypeSelectionLoading = true;
      this.isCatalogSelectionLoading = true;

      this.configSelection = await Api.katalogService.getConfigSelection(
        this.klasse,
        this.config
      );

      if (this.configSelection?.showConfigSelection) {
        let dateValue =
          this.config.get("deliveryDate") ??
          new Date().toISOString();

        await this.setConfigValue("deliveryDate", dateValue, false);
      }

      this.isConfigSelectionLoading = false

      await this.loadTypeSelection();
    }
  }

  @action.bound
  public async loadTypeSelection() {
    if (this.klasse) {
      this.isTypeSelectionLoading = true;
      this.isCatalogSelectionLoading = true;

      this.typeSelection = await Api.katalogService.getTypeSelection(
        this.klasse,
        this.config,
        this.filter,
        this.getCompleteFilter(this.filter)
      );

      this.isTypeSelectionLoading = false;

      this.catalogSelection = await Api.katalogService.getCatalogSelection(
        this.klasse,
        this.filter,
        this.getCompleteFilter(this.filter)
      );

      this.isCatalogSelectionLoading = false;
    }

    await this.saveToken();
  }

  public async saveToken() {
    if (this.token) {
      let data = {
        klasse: this.klasse,
        config: this.config,
        filter: this.filter,
        completeFilter: this.getCompleteFilter(this.filter),
      };

      if (Object.keys(this.codechain).length > 0) {
        data["codechain"] = this.codechain;
      }

      if (Object.keys(this.sa_data).length > 0) {
        data["sa_data"] = this.sa_data;
      }

      await Api.katalogService.updateSession(this.token, data);
    }
  }

  @action.bound
  public setConfigValue(key, value, reload=true) {
    this.config.set(key, value);
    this.filter.replace({});
    this.codechain.replace({});
    this.sa_data.replace({});
    //If reload is set, we load all areas, otherwise we just save the token
    if (reload) {
      return this.loadConfigSelection();
    }
    return this.saveToken();
  }

  @action.bound
  public setFilterValue(key, value) {
    this.filter.set(key, value);
    this.codechain.replace({});
    this.sa_data.replace({});
    return this.loadTypeSelection();
  }

  private getCompleteFilter(filter) {
    let f = JSON.parse(JSON.stringify(filter));
    let result = {};
    if (this.typeSelection) {
      this.typeSelection.selects.forEach((select) => {
        let value: any = null;
        if (select.data.length == 1) {
          value = select.data[0].key;
        } else if (f[select.key]) {
          value = f[select.key];
        }

        if (value) {
          result[select.key] = value;
        }
      });
    }

    return result;
  }

  public getToken() {
    return this.token;
  }

  public hasError(catalogId) {
    if (this.catalogSelection) {
      let entry = this.catalogSelection.entries.find(
        (select) => select.id == catalogId
      );

      if (
        entry &&
        entry["required_message"] &&
        entry["required_message"].length > 0
      ) {
        return true;
      }
    }

    return false;
  }

  public getError(catalogId) {
    if (this.catalogSelection) {
      let entry = this.catalogSelection.entries.find(
        (select) => select.id == catalogId
      );

      if (
        entry &&
        entry["required_message"] &&
        entry["required_message"].length > 0
      ) {
        return entry["required_message"];
      }
    }

    return "";
  }

  public getCount(catalogId) {
    if (this.catalogSelection) {
      let entry = this.catalogSelection.entries.find(
        (select) => select.id == catalogId
      );

      if (entry && entry.hasOwnProperty("count")) {
        return entry.count;
      }
    }

    return 0;
  }

  public hasData(catalogId) {
    if (this.catalogSelection) {
      let entry = this.catalogSelection.entries.find(
        (select) => select.id == catalogId
      );

      return (
        entry &&
        (entry.count > 0 || (entry.count == 0 && entry.count_nouserfilter == 0))
      );
    } else {
      return false;
    }
  }

  public isFilterSatisfied(catalogId) {
    if (this.catalogSelection) {
      let entry = this.catalogSelection.entries.find(
        (select) => select.id == catalogId
      );

      return entry && entry.required_filter_satisfied;
    } else {
      return false;
    }
  }
}
